import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useCustomerAuth } from '../../../contexts/CustomerAuthContext';
import { useSiteBrand } from '../../../contexts/SiteBrandContext';
import { getResendSecondsLeft, canResendOTP } from '../../../services/emailService';

const FLARE_RED = '#c4151c';

const EmailIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const LockIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

const UserIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const PhoneIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);

const OTPIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const BackIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
  </svg>
);

function AuthBrandingLeft({ eyebrow = 'Welcome back to', subline }) {
  const brand = useSiteBrand();
  return (
    <div className="relative lg:w-1/2 flex flex-col items-center justify-center p-8 py-12 bg-slate-50 text-center overflow-hidden">
      <div className="absolute -top-16 -left-16 w-72 h-72 bg-red-100 rounded-full opacity-50"></div>
      <div className="absolute -bottom-24 -right-10 w-96 h-96 border-4 border-red-100 rounded-full opacity-50"></div>
      <div className="relative z-10 flex-grow flex flex-col items-center justify-center">
        <h2 className="text-2xl md:text-3xl font-light text-gray-600 mb-4">{eyebrow}</h2>
        <img src={brand.userLogo || brand.logo} alt="Flare Magazine Logo" className="h-32 md:h-44 w-auto mx-auto" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/Flare Logo.png'; }} />
        {subline && <p className="mt-6 text-gray-600 max-w-md mx-auto font-medium">{subline}</p>}
      </div>
      <div className="relative z-10 w-full mt-12 pt-8 border-t border-slate-200">
        <p className="text-sm font-semibold text-gray-500 mb-4">Also a part of the Mix FM Network</p>
        <div className="flex justify-center items-center gap-x-4 sm:gap-x-6">
          <img src="/Mix FM Logo 99.png" alt="Mix FM 99" className="h-16 w-auto" />
          <img src="/Mix FM Logo 100.png" alt="Mix FM 100" className="h-16 w-auto" />
          <img src="/Mix FM Logo 96.png" alt="Mix FM 96" className="h-16 w-auto" />
        </div>
      </div>
    </div>
  );
}

function useCountdown(initialSeconds = 0) {
  const [seconds, setSeconds] = useState(initialSeconds);
  useEffect(() => {
    if (seconds <= 0) return;
    const id = setInterval(() => setSeconds(s => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [seconds]);
  return [seconds, setSeconds];
}

function useResendTimer({ email, type }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [email, type]);
  void tick;
  const can = !email ? false : canResendOTP({ email, type });
  const left = !email ? 0 : getResendSecondsLeft({ email, type });
  return { canResend: can, secondsLeft: left };
}

function OTPInputBoxes({ value, onChange, length = 6, autoFocusFirst = true, onComplete }) {
  const refs = React.useRef([]);
  const boxes = Array.from({ length }).map((_, i) => value[i] || '');

  const focusAt = (i) => {
    const el = refs.current[i];
    if (el) {
      el.focus();
      el.select?.();
    }
  };

  const applyValue = (next) => {
    const clean = String(next || '').replace(/\D/g, '').slice(0, length);
    onChange(clean);
    if (clean.length >= length && typeof onComplete === 'function') {
      setTimeout(() => onComplete(clean), 0);
    }
  };

  const setDigit = (i, raw) => {
    const digits = String(raw || '').replace(/\D/g, '');
    if (!digits) {
      const next = `${value.slice(0, i)}${value.slice(i + 1)}`;
      onChange(next);
      return;
    }
    if (digits.length > 1) {
      // paste / autofill into one box
      const merged = `${value.slice(0, i)}${digits}`.replace(/\D/g, '').slice(0, length);
      applyValue(merged);
      focusAt(Math.min(merged.length, length - 1));
      return;
    }
    const next = `${value.slice(0, i)}${digits}${value.slice(i + 1)}`.slice(0, length);
    applyValue(next);
    if (i < length - 1) focusAt(i + 1);
  };

  return (
    <div className="flex gap-2 sm:gap-3 justify-center my-4">
      {boxes.map((ch, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          autoFocus={autoFocusFirst && i === 0}
          value={ch}
          onChange={(e) => setDigit(i, e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Backspace') {
              e.preventDefault();
              if (boxes[i]) {
                const next = `${value.slice(0, i)}${value.slice(i + 1)}`;
                onChange(next);
              } else if (i > 0) {
                const next = `${value.slice(0, i - 1)}${value.slice(i)}`;
                onChange(next);
                focusAt(i - 1);
              }
            } else if (e.key === 'ArrowLeft' && i > 0) {
              e.preventDefault();
              focusAt(i - 1);
            } else if (e.key === 'ArrowRight' && i < length - 1) {
              e.preventDefault();
              focusAt(i + 1);
            }
          }}
          onPaste={(e) => {
            e.preventDefault();
            const pasted = (e.clipboardData.getData('text') || '').replace(/\D/g, '');
            if (!pasted) return;
            applyValue(pasted);
            focusAt(Math.min(pasted.length, length) - 1);
          }}
          data-otp-idx={i}
          className="w-11 h-14 sm:w-12 sm:h-14 text-center text-2xl font-extrabold border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#c4151c] focus:border-[#c4151c]"
          style={{ caretColor: FLARE_RED, borderColor: boxes[i] ? FLARE_RED : '#d1d5db', color: FLARE_RED }}
        />
      ))}
    </div>
  );
}

export function CustomerLoginPage() {
  const { login } = useCustomerAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success('Welcome back! 🎉');
      const redirect = new URLSearchParams(location.search).get('redirect') || '/account';
      setTimeout(() => navigate(redirect, { replace: true }), 50);
    } catch (err) {
      toast.error(err.message || 'Login failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-white">
      <AuthBrandingLeft
        eyebrow="Welcome back to"
        subline="Pakistan's best read. Delivered your way."
      />
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 py-12">
        <div className="w-full max-w-sm">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Log in to your <span style={{ color: FLARE_RED }}>Customer Account</span>
          </h2>
          <p className="text-gray-500 mb-8">
            Track your subscriptions, upcoming deliveries & download invoices.
          </p>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
              <div className="relative mt-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><EmailIcon /></div>
                <input
                  type="email" id="email" required
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#c4151c] focus:border-transparent"
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
                <Link to="/forgot-password"
                  className="text-xs font-semibold hover:underline"
                  style={{ color: FLARE_RED }}>
                  Forgot password?
                </Link>
              </div>
              <div className="relative mt-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><LockIcon /></div>
                <input
                  type={showPassword ? 'text' : 'password'} id="password" required
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="block w-full pl-10 pr-16 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#c4151c] focus:border-transparent"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm font-semibold text-gray-600">
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
            <button
              type="submit" disabled={submitting}
              className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg text-white font-bold text-base shadow-md transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ backgroundColor: FLARE_RED }}
            >
              {submitting ? 'Logging in…' : 'Log In'}
            </button>
          </form>
          <div className="mt-8 pt-6 border-t border-gray-100 text-center space-y-2 text-sm">
            <p className="text-gray-600">
              Don't have an account?{' '}
              <Link to="/register" className="font-bold hover:underline" style={{ color: FLARE_RED }}>Create one here →</Link>
            </p>
            <p className="text-gray-400 text-xs">
              <Link to="/contact" className="hover:underline hover:text-gray-600">Need help? Contact support</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CustomerRegisterPage() {
  const { sendRegisterOTP, registerWithOTP } = useCustomerAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const preEmail = params.get('email') || '';
  const preName = params.get('name') || '';
  const prePhone = params.get('phone') || '';

  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: preName, email: preEmail, phone: prePhone, city: '', password: '', confirm: '',
    acceptTerms: false, newsletter: true,
  });
  const [otp, setOtp] = useState('');
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);

  const { canResend, secondsLeft } = useResendTimer({ email: form.email, type: 'VERIFY' });

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSendOTP = async (e) => {
    if (e) e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.password) {
      toast.error('Please fill in name, email and password first.');
      return;
    }
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters.');
      return;
    }
    if (form.password !== form.confirm) {
      toast.error('Passwords do not match');
      return;
    }
    if (form.phone && form.phone.replace(/\D/g, '').length < 10) {
      toast.error('Please enter a valid phone number (at least 10 digits).');
      return;
    }
    if (!form.acceptTerms) {
      toast.error('Please accept the Terms of Service to continue.');
      return;
    }
    setOtpLoading(true);
    try {
      await sendRegisterOTP(form.email, form.name, form.phone);
      setStep(2);
      setOtp('');
      toast.success(`Verification code sent to ${form.email}`);
    } catch (err) {
      toast.error(err.message || 'Could not send OTP');
    } finally { setOtpLoading(false); }
  };

  const handleResend = async () => {
    if (!canResend) return;
    try {
      await sendRegisterOTP(form.email, form.name, form.phone);
      toast.success('New verification code sent to your email!');
    } catch (err) {
      toast.error(err.message || 'Could not resend OTP');
    }
  };

  const handleVerifyAndCreate = async (e, codeOverride) => {
    if (e?.preventDefault) e.preventDefault();
    if (submitting) return;
    const code = String(codeOverride || otp || '').replace(/\D/g, '');
    if (code.length !== 6) {
      toast.error('Please enter the 6-digit code.');
      return;
    }
    setSubmitting(true);
    try {
      const created = await registerWithOTP({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        city: form.city.trim(),
        password: form.password,
        newsletter_optin: !!form.newsletter,
        source: params.get('source') || 'register',
      }, code);
      toast.success(`Welcome to Flare, ${created.name}! 🎉`);
      setTimeout(() => navigate('/account', { replace: true }), 100);
    } catch (err) {
      toast.error(err.message || 'Registration failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-white">
      <AuthBrandingLeft
        eyebrow="Join the"
        subline="Get doorstep delivery of every issue + digital PDF copies + exclusive member discounts."
      />
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 py-12">
        <div className="w-full max-w-md">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Create your <span style={{ color: FLARE_RED }}>Flare Account</span>
          </h2>
          {step === 1 ? (
            <>
              <p className="text-gray-500 mb-8">
                Step 1 of 2 · Takes under 60 seconds. We'll email you a verification code.
              </p>
              <form onSubmit={handleSendOTP} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Full Name *</label>
                  <div className="relative mt-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><UserIcon /></div>
                    <input required value={form.name} onChange={e => set('name', e.target.value)}
                      placeholder="Ahmed Khan"
                      className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#c4151c] focus:border-transparent" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email *</label>
                  <div className="relative mt-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><EmailIcon /></div>
                    <input required type="email" value={form.email} onChange={e => set('email', e.target.value)}
                      placeholder="you@email.com"
                      className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#c4151c] focus:border-transparent" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Phone / WhatsApp</label>
                    <div className="relative mt-1">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><PhoneIcon /></div>
                      <input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)}
                        placeholder="0300 1234567"
                        className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#c4151c] focus:border-transparent" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">City</label>
                    <input type="text" value={form.city} onChange={e => set('city', e.target.value)}
                      placeholder="Lahore"
                      className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#c4151c] focus:border-transparent" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Password * (min 6 chars)</label>
                  <div className="relative mt-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><LockIcon /></div>
                    <input required minLength={6} type={show ? 'text' : 'password'}
                      value={form.password} onChange={e => set('password', e.target.value)}
                      placeholder="••••••••"
                      className="block w-full pl-10 pr-16 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#c4151c] focus:border-transparent" />
                    <button type="button" onClick={() => setShow(!show)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm font-semibold text-gray-600">
                      {show ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Confirm Password *</label>
                  <div className="relative mt-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><LockIcon /></div>
                    <input required minLength={6} type="password"
                      value={form.confirm} onChange={e => set('confirm', e.target.value)}
                      placeholder="Repeat password"
                      className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#c4151c] focus:border-transparent" />
                  </div>
                </div>
                <label className="flex items-start gap-2 text-sm text-gray-600 cursor-pointer">
                  <input type="checkbox" checked={form.newsletter} onChange={e => set('newsletter', e.target.checked)} className="mt-1" style={{ accentColor: FLARE_RED }} />
                  <span>Email me new issue drops and subscriber offers.</span>
                </label>
                <label className="flex items-start gap-2 text-sm text-gray-600 cursor-pointer">
                  <input type="checkbox" checked={form.acceptTerms} onChange={e => set('acceptTerms', e.target.checked)} className="mt-1" style={{ accentColor: FLARE_RED }} required />
                  <span>
                    I agree to the{' '}
                    <Link to="/terms-of-service" className="font-semibold hover:underline" style={{ color: FLARE_RED }}>Terms</Link>
                    {' '}and{' '}
                    <Link to="/privacy-policy" className="font-semibold hover:underline" style={{ color: FLARE_RED }}>Privacy Policy</Link>.
                  </span>
                </label>
                <button
                  type="submit" disabled={otpLoading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg text-white font-bold text-base shadow-md transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed mt-2"
                  style={{ backgroundColor: FLARE_RED }}
                >
                  {otpLoading ? 'Sending code…' : 'Send Verification Code →'}
                </button>
              </form>
              <div className="mt-6 text-center text-sm text-gray-600">
                Already have an account?{' '}
                <Link to="/login" className="font-bold hover:underline" style={{ color: FLARE_RED }}>Log in →</Link>
              </div>
            </>
          ) : (
            <>
              <p className="text-gray-500 mb-2">Step 2 of 2 · Verify your email address</p>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-gray-700">
                  We sent a 6-digit code to{' '}
                  <b style={{ color: FLARE_RED }}>{form.email}</b>. Check your inbox (and spam folder), then enter it below.
                </p>
              </div>
              <form onSubmit={handleVerifyAndCreate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <OTPIcon /> Verification Code (6 digits)
                  </label>
                  <OTPInputBoxes
                    value={otp}
                    onChange={setOtp}
                    length={6}
                    onComplete={(code) => handleVerifyAndCreate(null, code)}
                  />
                </div>
                <div className="text-sm text-center space-y-1 pt-2">
                  <p className="text-gray-600">
                    Didn't get the email?{' '}
                    {canResend ? (
                      <button type="button" onClick={handleResend}
                        className="font-bold hover:underline"
                        style={{ color: FLARE_RED }}>
                        Resend code
                      </button>
                    ) : (
                      <span className="text-gray-500 font-semibold">
                        Resend available in {secondsLeft}s
                      </span>
                    )}
                  </p>
                  <p className="text-xs">
                    <button type="button" onClick={() => { setStep(1); setOtp(''); }}
                      className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-700 hover:underline">
                      <BackIcon /> Change email / details
                    </button>
                  </p>
                </div>
                <button
                  type="submit" disabled={submitting || otp.length !== 6}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg text-white font-bold text-base shadow-md transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed mt-2"
                  style={{ backgroundColor: FLARE_RED }}
                >
                  {submitting ? 'Creating account…' : 'Verify & Create Account'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function CustomerForgotPasswordPage() {
  const { sendForgotOTP, resetPasswordWithOTP } = useCustomerAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const { canResend, secondsLeft } = useResendTimer({ email, type: 'FORGOT' });

  const handleSend = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await sendForgotOTP(email);
      setStep(2);
      setOtp('');
      toast.success(`Password reset code sent to ${email}`);
    } catch (err) {
      toast.error(err.message || 'Could not send OTP');
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    if (!canResend) return;
    try {
      await sendForgotOTP(email);
      toast.success('New reset code sent to your email!');
    } catch (err) {
      toast.error(err.message || 'Could not resend');
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error('Please enter the 6-digit code.');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    if (String(password).length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await resetPasswordWithOTP(email, otp, password);
      toast.success('Password changed successfully! Please log in with your new password.');
      setTimeout(() => navigate('/login', { replace: true }), 50);
    } catch (err) {
      toast.error(err.message || 'Could not reset password');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-white">
      <AuthBrandingLeft eyebrow="Reset your" subline="We'll email you a one-time code to set a new password securely." />
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 py-12">
        <div className="w-full max-w-md">
          <Link to="/login" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4 hover:underline">
            <BackIcon /> Back to login
          </Link>
          {step === 1 ? (
            <>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                Forgot your <span style={{ color: FLARE_RED }}>password?</span>
              </h2>
              <p className="text-gray-500 mb-8">
                Step 1 of 3 · Enter your email address. We'll send a password reset code to it.
              </p>
              <form onSubmit={handleSend} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email Address</label>
                  <div className="relative mt-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><EmailIcon /></div>
                    <input required type="email" value={email} onChange={e => setEmail(e.target.value)}
                      placeholder="you@email.com"
                      className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#c4151c] focus:border-transparent" />
                  </div>
                </div>
                <button type="submit" disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg text-white font-bold text-base shadow-md transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ backgroundColor: FLARE_RED }}>
                  {loading ? 'Sending code…' : 'Send Reset Code →'}
                </button>
              </form>
            </>
          ) : step === 2 ? (
            <>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                Enter <span style={{ color: FLARE_RED }}>verification code</span>
              </h2>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-gray-700">
                  We emailed a 6-digit reset code to{' '}
                  <b style={{ color: FLARE_RED }}>{email}</b>. Check inbox / spam, then enter it below.
                </p>
              </div>
              <form onSubmit={(e) => { e.preventDefault(); if (otp.length === 6) setStep(3); else toast.error('Please enter 6 digits.'); }} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <OTPIcon /> 6-digit Reset Code
                  </label>
                  <OTPInputBoxes
                    value={otp}
                    onChange={setOtp}
                    length={6}
                    onComplete={() => setStep(3)}
                  />
                </div>
                <div className="text-sm text-center pt-2 space-y-1">
                  <p className="text-gray-600">
                    Didn't get the code?{' '}
                    {canResend ? (
                      <button type="button" onClick={handleResend} className="font-bold hover:underline" style={{ color: FLARE_RED }}>
                        Resend code
                      </button>
                    ) : (
                      <span className="text-gray-500 font-semibold">Resend available in {secondsLeft}s</span>
                    )}
                  </p>
                  <button type="button" onClick={() => setStep(1)}
                    className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 hover:underline">
                    <BackIcon /> Use a different email
                  </button>
                </div>
                <button type="submit" disabled={otp.length !== 6}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg text-white font-bold text-base shadow-md transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed mt-2"
                  style={{ backgroundColor: FLARE_RED }}>
                  Verify Code →
                </button>
              </form>
            </>
          ) : (
            <>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                Set your <span style={{ color: FLARE_RED }}>new password</span>
              </h2>
              <p className="text-gray-500 mb-8">Step 3 of 3 · Choose a strong, memorable password.</p>
              <form onSubmit={handleReset} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700">New Password * (min 6 chars)</label>
                  <div className="relative mt-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><LockIcon /></div>
                    <input required minLength={6} type={show ? 'text' : 'password'}
                      value={password} onChange={e => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="block w-full pl-10 pr-16 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#c4151c] focus:border-transparent" />
                    <button type="button" onClick={() => setShow(!show)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm font-semibold text-gray-600">
                      {show ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Confirm New Password *</label>
                  <div className="relative mt-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><LockIcon /></div>
                    <input required minLength={6} type="password"
                      value={confirm} onChange={e => setConfirm(e.target.value)}
                      placeholder="Repeat new password"
                      className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#c4151c] focus:border-transparent" />
                  </div>
                </div>
                <button type="submit" disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg text-white font-bold text-base shadow-md transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed mt-2"
                  style={{ backgroundColor: FLARE_RED }}>
                  {loading ? 'Updating password…' : 'Update Password ✓'}
                </button>
              </form>
            </>
          )}
          <div className="mt-8 pt-6 border-t border-gray-100 text-center space-y-2 text-sm">
            <p className="text-gray-600">
              Remembered it?{' '}
              <Link to="/login" className="font-bold hover:underline" style={{ color: FLARE_RED }}>
                Go back to login →
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CustomerLoginPage;
